import { useState, useCallback } from 'react';
import {
  addFamilyMember,
  updateFamilyMember,
  deactivateFamilyMember,
  getFamilyMembers
} from '../services/subscriberWalletService';
import {
  formatRelationshipForApi,
  formatGenderForApi,
  calculateAgeFromDob
} from '../utils/subscriberHelpers';

export const useFamilyMembers = (id, currentRole, family, setFamily, showNotification) => {
  const [addingMember, setAddingMember] = useState(false);
  const [updatingMember, setUpdatingMember] = useState(false);
  const [deactivatingMember, setDeactivatingMember] = useState(false);

  const addMember = useCallback(async (memberData) => {
    if (!id || !currentRole) {
      showNotification('Subscriber ID or role is missing', 'error');
      return false;
    }

    const formattedRelation = formatRelationshipForApi(memberData.relationship);
    if (!formattedRelation || formattedRelation.trim() === '') {
      showNotification('Invalid relationship selected. Please choose a valid relationship from the list.', 'error');
      return false;
    }

    setAddingMember(true);
    try {
      const apiData = {
        name: memberData.name.trim(),
        relation: formattedRelation,
        dob: memberData.dob,
        gender: formatGenderForApi(memberData.gender)
      };

      const calculatedAge = Number.isFinite(memberData.age) ? memberData.age : calculateAgeFromDob(memberData.dob);
      if (calculatedAge !== null && calculatedAge !== undefined) {
        apiData.age = calculatedAge;
      }

      if (memberData.mobile && memberData.mobile.trim()) {
        const cleanedMobile = memberData.mobile.replace(/\D/g, '');
        if (cleanedMobile.length === 10) {
          apiData.contact_number = cleanedMobile;
        }
      }

      if (memberData.email && memberData.email.trim()) {
        apiData.email = memberData.email.trim();
      }

      const response = await addFamilyMember(id, currentRole, apiData);

      if (response && response.metadata && response.metadata.api_status === true) {
        // Step 1: Call API to save to database - success
        // Step 2: Refresh family members from database (with error handling)
        try {
          const familyData = await getFamilyMembers(id, currentRole);
          
          // Handle different response formats for family data
          let updatedFamilyData = null;
          if (familyData?.msg?.items && Array.isArray(familyData.msg.items)) {
            updatedFamilyData = familyData.msg.items;
          } else if (familyData?.msg && Array.isArray(familyData.msg)) {
            updatedFamilyData = familyData.msg;
          } else if (familyData?.data?.items && Array.isArray(familyData.data.items)) {
            updatedFamilyData = familyData.data.items;
          } else if (Array.isArray(familyData)) {
            updatedFamilyData = familyData;
          } else if (familyData?.data && Array.isArray(familyData.data)) {
            updatedFamilyData = familyData.data;
          }
          
          if (updatedFamilyData) {
            setFamily(updatedFamilyData.map((m) => ({
              ...m,
              age: calculateAgeFromDob(m.dob || m.date_of_birth || m.dateOfBirth)
            })));
          }
        } catch (refreshError) {
          // Don't fail the entire operation just because refresh failed
          console.error('Failed to refresh family list:', refreshError);
        }
        
        // Show success message with member name
        const memberName = response.msg?.name || response.msg?.member?.name || 'Family member';
        showNotification(`${memberName} added successfully! The family member has been saved to the database.`, 'success');
        return true;
      } else {
        const errorMsg = response?.msg?.error || response?.msg?.message || response?.error || 'Failed to add family member';
        throw new Error(errorMsg);
      }
    } catch (error) {
      showNotification(error.message || 'Failed to add family member', 'error');
      return false;
    } finally {
      setAddingMember(false);
    }
  }, [id, currentRole, setFamily, showNotification]);

  const updateMember = useCallback(async (memberId, memberData) => {
    if (!id || !currentRole || !memberId) {
      showNotification('Missing required information', 'error');
      return false;
    }

    const formattedRelation = formatRelationshipForApi(memberData.relationship);
    if (!formattedRelation || formattedRelation.trim() === '') {
      showNotification('Invalid relationship selected', 'error');
      return false;
    }

    setUpdatingMember(true);
    try {
      const apiData = {
        name: memberData.name.trim(),
        relation: formattedRelation,
        dob: memberData.dob,
        gender: formatGenderForApi(memberData.gender)
      };

      const calculatedAge = Number.isFinite(memberData.age) ? memberData.age : calculateAgeFromDob(memberData.dob);
      if (calculatedAge !== null && calculatedAge !== undefined) {
        apiData.age = calculatedAge;
      }

      if (memberData.mobile && memberData.mobile.trim()) {
        const cleanedMobile = memberData.mobile.replace(/\D/g, '');
        if (cleanedMobile.length === 10) {
          apiData.contact_number = cleanedMobile;
        }
      }

      if (memberData.email && memberData.email.trim()) {
        apiData.email = memberData.email.trim();
      }

      const response = await updateFamilyMember(id, memberId, currentRole, apiData);

      if (response && response.metadata && response.metadata.api_status === true) {
        // Refresh family list from API to ensure all fields including status are preserved
        try {
          const familyData = await getFamilyMembers(id, currentRole);
          let updatedFamilyData = null;
          if (familyData && familyData.msg && Array.isArray(familyData.msg.items)) {
            updatedFamilyData = familyData.msg.items;
          } else if (familyData && familyData.data && Array.isArray(familyData.data.items)) {
            updatedFamilyData = familyData.data.items;
          } else if (Array.isArray(familyData)) {
            updatedFamilyData = familyData;
          } else if (familyData && familyData.msg && Array.isArray(familyData.msg)) {
            updatedFamilyData = familyData.msg;
          }
          
          if (updatedFamilyData) {
            setFamily(updatedFamilyData.map((m) => ({
              ...m,
              age: calculateAgeFromDob(m.dob || m.date_of_birth || m.dateOfBirth)
            })));
          }
        } catch (refreshError) {
          // Don't fail the entire operation just because refresh failed
          console.error('Failed to refresh family list:', refreshError);
        }
        
        showNotification('Family member updated successfully!', 'success');
        return true;
      } else {
        const errorMsg = response?.msg?.error || response?.msg?.message || response?.error || 'Failed to update family member';
        throw new Error(errorMsg);
      }
    } catch (error) {
      showNotification(error.message || 'Failed to update family member', 'error');
      return false;
    } finally {
      setUpdatingMember(false);
    }
  }, [id, currentRole, setFamily, showNotification]);

  const deactivateMember = useCallback(async (memberId, reasonCode) => {
    if (!id || !currentRole || !memberId) {
      showNotification('Missing required information', 'error');
      return false;
    }

    if (!reasonCode || !reasonCode.trim()) {
      showNotification('Reason code is required', 'error');
      return false;
    }

    setDeactivatingMember(true);
    try {
      const response = await deactivateFamilyMember(id, memberId, currentRole, reasonCode);

      if (response && response.metadata && response.metadata.api_status === true) {
        setFamily(prev => prev.map(m => 
          (m.member_id || m.id) === memberId 
            ? { ...m, status: 'Inactive' }
            : m
        ));
        showNotification('Family member deactivated successfully!', 'success');
        return true;
      } else {
        const errorMsg = response?.msg?.error || response?.msg?.message || 'Failed to deactivate family member';
        throw new Error(errorMsg);
      }
    } catch (error) {
      showNotification(error.message || 'Failed to deactivate family member', 'error');
      return false;
    } finally {
      setDeactivatingMember(false);
    }
  }, [id, currentRole, setFamily, showNotification]);

  return {
    addingMember,
    updatingMember,
    deactivatingMember,
    addMember,
    updateMember,
    deactivateMember
  };
};

